import type { CSSProperties } from 'react';
import type { SiteInfo } from '@/types';

export const DEFAULT_PORTRAIT_GRADIENT = [
  '#ff7e5f',
  '#feb47b',
  '#86a8e7',
  '#91eae4',
] as const;

const BLEND_MODES = new Set([
  'normal',
  'multiply',
  'screen',
  'overlay',
  'darken',
  'lighten',
  'color-dodge',
  'color-burn',
  'hard-light',
  'soft-light',
  'difference',
  'exclusion',
  'hue',
  'saturation',
  'color',
  'luminosity',
]);

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, n));
}

function normalizeUnit(value: unknown, fallback: number): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return clamp(n, 0, 1);
}

function normalizeRange(
  value: unknown,
  fallback: number,
  min: number,
  max: number,
): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return clamp(n, min, max);
}

export function portraitSurfaceStyle(info: SiteInfo | null): CSSProperties {
  const invert = info?.portraitInvert !== false;
  const grayscale =
    info?.portraitGrayscale !== undefined ? Boolean(info.portraitGrayscale) : invert;
  const brightness = normalizeRange(info?.portraitBrightness, 1, 0.4, 1.8);
  const contrast = normalizeRange(info?.portraitContrast, 1, 0.4, 1.8);
  const saturate = normalizeRange(info?.portraitSaturate, 1, 0, 2.5);
  const sepia = normalizeUnit(info?.portraitSepia, 0);
  const blur = normalizeRange(info?.portraitBlur, 0, 0, 16);
  const hueRotate = normalizeRange(info?.portraitHueRotate, 0, 0, 360);
  const vignette = Boolean(info?.portraitVignette);
  const gradientOn = info?.portraitGradientEnabled !== false;
  const colors = Array.isArray(info?.portraitGradient)
    ? info!.portraitGradient!.filter(Boolean)
    : [];
  const [c1, c2, c3, c4] = [
    colors[0] || DEFAULT_PORTRAIT_GRADIENT[0],
    colors[1] || DEFAULT_PORTRAIT_GRADIENT[1],
    colors[2] || DEFAULT_PORTRAIT_GRADIENT[2],
    colors[3] || DEFAULT_PORTRAIT_GRADIENT[3],
  ];
  const blend = String(info?.portraitBlendMode || 'exclusion').toLowerCase();
  const opacity = normalizeUnit(info?.portraitGradientOpacity, 1);
  const zoom = normalizeRange(info?.portraitZoom, 1, 1, 3);
  const focusX = normalizeRange(info?.portraitFocusX, 50, 0, 100);
  const focusY = normalizeRange(info?.portraitFocusY, 50, 0, 100);

  const parts: string[] = [];
  if (invert) parts.push('invert(1)');
  if (grayscale) parts.push('grayscale(1)');
  if (brightness !== 1) parts.push(`brightness(${brightness})`);
  if (contrast !== 1) parts.push(`contrast(${contrast})`);
  if (saturate !== 1) parts.push(`saturate(${saturate})`);
  if (sepia > 0) parts.push(`sepia(${sepia})`);
  if (hueRotate !== 0) parts.push(`hue-rotate(${hueRotate}deg)`);
  if (blur > 0) parts.push(`blur(${blur}px)`);

  return {
    ['--portrait-filter' as string]: parts.length ? parts.join(' ') : 'none',
    ['--portrait-c1' as string]: c1,
    ['--portrait-c2' as string]: c2,
    ['--portrait-c3' as string]: c3,
    ['--portrait-c4' as string]: c4,
    ['--portrait-blend-mode' as string]: BLEND_MODES.has(blend) ? blend : 'exclusion',
    ['--portrait-gradient-opacity' as string]: gradientOn ? String(opacity) : '0',
    ['--portrait-vignette-opacity' as string]: vignette ? '0.55' : '0',
    ['--portrait-zoom' as string]: String(zoom),
    ['--portrait-focus-x' as string]: `${focusX}%`,
    ['--portrait-focus-y' as string]: `${focusY}%`,
  };
}
